/**
 * OutcomesPage — J1-B
 *
 * Day-0 Strategic Spine: Outcomes CRUD.
 *
 * Strategy domain is pre-selected by default (PRD v3.4 GAP 4).
 * Strategy is domain_id=1 (STRATEGY enum constant from roros-enums).
 * Founder can override the domain selection.
 *
 * Compliance: ART-01 (definition_of_done = evidence anchor),
 *             ART-04 (Outcomes category live).
 *
 * PRD §12.3.4 task 1.
 *
 * AC-FW3a-4-1: Detail modal + CascadeWorkItemsSection merged in from
 * AnnualOutcomesPage. OutcomeRow now opens the detail modal on click (in
 * addition to Edit/Delete actions). CascadeWorkItemsSection uses the existing
 * /api/v1/outcomes/:id/cascade-work-items endpoint; returns an empty list for
 * non-annual outcomes, which renders as the empty-state message.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Table, type TableColumn } from '@/components/ui/Table';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { Plus, Target, Pencil, Trash2, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react';
import { t } from '@/lib/i18n';
import { outcomeStatusVariant, outcomeStatusLabel, workItemStatusVariant, workItemStatusLabel } from '@/lib/roros/outcomeHelpers';
import {
  useRorosOutcomes,
  useRorosOutcomeCreate,
  useRorosOutcomeUpdate,
  useRorosOutcomeDelete,
  useOutcomeCascadeWorkItems,
} from '@/hooks/roros/artifacts';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosTenantSettings } from '@/hooks/roros/launchpad';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { visibleTimeHorizons } from '@/lib/timeHorizons';
import type { RorosOutcomeDTO } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { CascadeWorkItemDTO } from '@/services/roros/artifacts.service';
import { OutcomeFormDialog } from './OutcomeFormDialog';
import { formatDate } from '@/lib/localeFormatters';

// Strategy domain_id = 1 (STRATEGY enum). Pre-selected per PRD v3.4 GAP 4.
const STRATEGY_DOMAIN_ID_INT = 1;

/** URL param key for deep-link editing (G3 URL routing pilot). */
const OUTCOME_URL_KEY = 'edit-outcome';

// ─── Status badge colours (legacy — kept for OutcomeRow backward compat) ───────

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'achieved': return 'success';
    case 'missed': return 'error';
    case 'archived': return 'default';
    default: return 'warning';
  }
}

// ─── Cascade Work Items section (AC-8.2 / AC-FW3a-4-1) ───────────────────────

function CascadeWorkItemsSection({ outcomeId }: { outcomeId: string }) {
  const { data, isLoading } = useOutcomeCascadeWorkItems(outcomeId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{t('Loading cascade items...')}</span>
      </div>
    );
  }

  const items = data ?? [];

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        {t('No cascade items yet — fire the cascade by completing an Annual Strategy Review meeting.')}
      </p>
    );
  }

  // AC-FW3a-2-M-DS-UI: DS Table component with typed columns
  const columns: TableColumn<CascadeWorkItemDTO>[] = [
    {
      key: 'domain',
      title: t('Domain'),
      render: (_val, item) => <span className="text-foreground">{item.domainName ?? item.domainId}</span>,
    },
    {
      key: 'assignee',
      title: t('Assignee'),
      render: (_val, item) => <span className="text-foreground">{item.assigneeName ?? t('Unassigned')}</span>,
    },
    {
      key: 'status',
      title: t('Status'),
      // AC-FW3a-2-LOW-7: workItemStatusVariant for correct badge colour
      // AC-FW3a-2-M-i18n: workItemStatusLabel for translated status text
      render: (_val, item) => (
        <Badge variant={workItemStatusVariant(item.status)}>
          {workItemStatusLabel(item.status)}
        </Badge>
      ),
    },
    {
      key: 'dueDate',
      title: t('Due'),
      render: (_val, item) => (
        <span className="text-muted-foreground text-xs">
          {item.dueDate ? formatDate(item.dueDate) : '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="overflow-x-auto">
      <Table
        data={items}
        columns={columns}
        searchable={false}
        pagination={false}
      />
    </div>
  );
}

// ─── Outcome Detail Modal (AC-8.2 / AC-FW3a-4-1) ─────────────────────────────

interface OutcomeDetailModalProps {
  outcome: RorosOutcomeDTO | null;
  onClose: () => void;
}

function OutcomeDetailModal({ outcome, onClose }: OutcomeDetailModalProps) {
  return (
    <Modal
      isOpen={!!outcome}
      onClose={onClose}
      title={outcome?.title ?? ''}
      size="lg"
    >
      {outcome && (
        <div className="space-y-5">
          {/* Status + Time Horizon */}
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant={outcomeStatusVariant(outcome.status)}>{outcomeStatusLabel(outcome.status)}</Badge>
            {outcome.timeHorizon && (
              <Badge variant="outline">{t(outcome.timeHorizon)}</Badge>
            )}
          </div>

          {/* Description */}
          {outcome.description && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('Description')}</p>
              <p className="text-sm text-foreground leading-relaxed">{outcome.description}</p>
            </div>
          )}

          {/* Definition of Done */}
          {outcome.definitionOfDone && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('Definition of Done')}</p>
              <p className="text-sm text-foreground leading-relaxed">{outcome.definitionOfDone}</p>
            </div>
          )}

          {/* Source Meeting */}
          {outcome.sourceMeetingId && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('Source Meeting')}</p>
              <Link
                to={`/roros/meetings/${outcome.sourceMeetingId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline"
              >
                {t('View meeting')}
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">{t('Created')}</p>
              <p className="text-foreground">{formatDate(outcome.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t('Updated')}</p>
              <p className="text-foreground">{formatDate(outcome.updatedAt)}</p>
            </div>
          </div>

          {/* Cascade Work Items section (AC-8.2) — non-annual outcomes render empty-state */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('Domain VP Cascade Items')}</p>
            <CascadeWorkItemsSection outcomeId={outcome.id} />
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Main page component ───────────────────────────────────────────────────

export function OutcomesPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingOutcome, setEditingOutcome] = useState<RorosOutcomeDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);

  const [filterDomainId, setFilterDomainId] = useState<string>('');
  const [filterHorizon, setFilterHorizon] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('active');
  // AC-FW3a-4-1: detail modal state
  const [detailOutcome, setDetailOutcome] = useState<RorosOutcomeDTO | null>(null);

  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_CREATE);
  const canEdit = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_EDIT);
  const canDelete = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_DELETE);

  const domainsQuery = useRorosDomains();
  const domains = (domainsQuery.data ?? []).filter((d: RorosDomainDTO) => d.isActive);

  const tenantSettingsQuery = useRorosTenantSettings();
  const planningCycle = (tenantSettingsQuery.data as { planning_cycle?: string } | undefined)?.planning_cycle;
  const timeHorizons = useMemo(
    () => visibleTimeHorizons(planningCycle).map((o) => ({ value: o.value, label: t(o.value) })),
    [planningCycle],
  );

  const strategyDomain = useMemo(
    () => domains.find((d) => d.domainId === STRATEGY_DOMAIN_ID_INT) ?? null,
    [domains],
  );

  const activeDomainId = filterDomainId || (strategyDomain?.id ?? (domains[0]?.id ?? ''));

  const outcomesParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (filterHorizon) p.time_horizon = filterHorizon;
    if (filterStatus && filterStatus !== 'all') p.status = filterStatus;
    return Object.keys(p).length > 0 ? p : undefined;
  }, [filterHorizon, filterStatus]);

  const outcomesQuery = useRorosOutcomes(activeDomainId, outcomesParams);
  const outcomes = (outcomesQuery.data ?? []) as RorosOutcomeDTO[];

  const createMutation = useRorosOutcomeCreate();
  const updateMutation = useRorosOutcomeUpdate();
  const deleteMutation = useRorosOutcomeDelete();

  // ─── URL routing (G3) — useFormSheetUrlRouting encapsulates
  // useSearchParams + ref-guard + useEffect boilerplate (F-Refinement-9).
  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: OUTCOME_URL_KEY,
    items: outcomes,
  });

  useEffect(() => {
    if (!deepLinkTarget) return;
    setEditingOutcome(deepLinkTarget);
    setIsFormOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink]);

  const openCreate = useCallback(() => { setEditingOutcome(null); setIsFormOpen(true); }, []);
  const openEdit = useCallback((outcome: RorosOutcomeDTO) => { setEditingOutcome(outcome); setIsFormOpen(true); }, []);
  const openDetail = useCallback((outcome: RorosOutcomeDTO) => { setDetailOutcome(outcome); }, []);
  const closeDetail = useCallback(() => { setDetailOutcome(null); }, []);

  const handleFormSubmit = useCallback(
    (data: Record<string, unknown>) => {
      const { domainId: formDomainId, ...payload } = data;  // A1: form now sends domainId
      if (editingOutcome) {
        updateMutation.mutate(
          { domainId: editingOutcome.domainId, id: editingOutcome.id, payload: payload as any },
          {
            onSuccess: () => { showToast.success(t('Outcome updated')); setIsFormOpen(false); },
            onError: (err) => showToast.error(err.message),
          },
        );
      } else {
        const domainId = (formDomainId as string) || activeDomainId;
        createMutation.mutate(
          { domainId, payload: payload as any },
          {
            onSuccess: () => { showToast.success(t('Outcome created')); setIsFormOpen(false); },
            onError: (err) => showToast.error(err.message),
          },
        );
      }
    },
    [editingOutcome, updateMutation, createMutation, activeDomainId],
  );

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    const outcome = outcomes.find((o) => o.id === deleteTarget.id);
    if (!outcome) return;
    deleteMutation.mutate(
      { domainId: outcome.domainId, id: deleteTarget.id },
      {
        onSuccess: () => { showToast.success(t('Outcome deleted')); setDeleteTarget(null); },
        onError: (err) => showToast.error(err.message),
      },
    );
  }, [deleteTarget, deleteMutation, outcomes]);

  const emptyCTA = canCreate ? (
    <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />} data-testid="outcomes-empty-cta">
      {t('Create your first 7-day Outcome')}
    </Button>
  ) : undefined;

  return (
    <RorosPage
      title={t('Outcomes')}
      icon={<CheckCircle2 className="w-5 h-5" />}
      breadcrumbs={[{ label: t('Outcomes'), path: '/roros/artifacts/outcomes' }]}
      actions={
        canCreate ? (
          <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />} data-testid="new-outcome-button">
            {t('New Outcome')}
          </Button>
        ) : undefined
      }
    >
      <div role="group" className="flex flex-wrap gap-3 mb-4" aria-label={t('Outcome filters')}>
        <div className="w-44">
          <Select
            id="outcome-domain-filter"
            value={filterDomainId || 'all'}
            onChange={(e) => setFilterDomainId(e.target.value === 'all' ? '' : e.target.value)}
            aria-label={t('Filter by domain')}
            options={[
              { value: 'all', label: t('All Domains') },
              ...domains.map((d) => ({ value: d.id, label: d.name })),
            ]}
          />
        </div>
        <div className="w-48">
          <Select
            id="outcome-horizon-filter"
            value={filterHorizon || 'all'}
            onChange={(e) => setFilterHorizon(e.target.value === 'all' ? '' : e.target.value)}
            aria-label={t('Filter by time horizon')}
            options={[
              { value: 'all', label: t('All Time Horizons') },
              ...timeHorizons.map((h) => ({ value: h.value, label: h.label })),
            ]}
          />
        </div>
        <div className="w-40">
          <Select
            id="outcome-status-filter"
            data-testid="outcome-status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label={t('Filter by status')}
            options={[
              { value: 'active', label: t('Active') },
              { value: 'achieved', label: t('Achieved') },
              { value: 'missed', label: t('Missed') },
              { value: 'archived', label: t('Archived') },
              { value: 'all', label: t('All Statuses') },
            ]}
          />
        </div>
      </div>

      <QueryPage
        query={outcomesQuery as any}
        emptyMessage={t('No outcomes yet')}
        emptyIcon={<Target className="h-8 w-8 text-muted-foreground" />}
        emptyAction={emptyCTA}
      >
        {(items) => (
          <div className="space-y-3" role="list" aria-label={t('Outcomes')}>
            {(items as RorosOutcomeDTO[]).map((outcome) => (
              <OutcomeRow
                key={outcome.id}
                outcome={outcome}
                domains={domains}
                canEdit={canEdit}
                canDelete={canDelete}
                onViewDetail={() => openDetail(outcome)}
                onEdit={() => openEdit(outcome)}
                onDelete={() => setDeleteTarget({ id: outcome.id, title: outcome.title })}
              />
            ))}
          </div>
        )}
      </QueryPage>

      {/* AC-FW3a-4-1: Outcome detail modal (merged from AnnualOutcomesPage) */}
      <OutcomeDetailModal outcome={detailOutcome} onClose={closeDetail} />

      {/* urlKey/urlId enable deep-link URL routing (G3 pilot). Only passed
          in edit mode — create mode has no stable id to write to the URL. */}
      <OutcomeFormDialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        editingOutcome={editingOutcome}
        domains={domains}
        strategyDomainId={strategyDomain?.id ?? (domains[0]?.id ?? '')}
        isLoading={createMutation.isPending || updateMutation.isPending}
        urlKey={editingOutcome ? OUTCOME_URL_KEY : undefined}
        urlId={editingOutcome?.id}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('Delete this outcome?')}
        description={deleteTarget ? `"${deleteTarget.title}"` : ''}
        confirmText={t('Delete')}
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </RorosPage>
  );
}

interface OutcomeRowProps {
  outcome: RorosOutcomeDTO;
  domains: RorosDomainDTO[];
  canEdit: boolean;
  canDelete: boolean;
  onViewDetail: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function OutcomeRow({ outcome, domains, canEdit, canDelete, onViewDetail, onEdit, onDelete }: OutcomeRowProps) {
  const domain = domains.find((d) => d.id === outcome.domainId);
  return (
    <Card
      className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
      role="listitem"
      tabIndex={0}
      aria-label={outcome.title}
      onClick={onViewDetail}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onViewDetail(); }}
      data-testid={`outcome-row-${outcome.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm truncate">{outcome.title}</h3>
            <Badge variant={statusVariant(outcome.status)}>{t(outcome.status)}</Badge>
            {outcome.timeHorizon && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{t(outcome.timeHorizon)}</span>
            )}
          </div>
          {outcome.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{outcome.description}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
            {domain && <span>{t('Domain')}: <span className="font-medium text-foreground">{domain.name}</span></span>}
            {outcome.definitionOfDone && (
              <span className="truncate max-w-[240px]">{t('Definition of Done')}: <span className="font-medium text-foreground">{outcome.definitionOfDone}</span></span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              aria-label={t('Edit')}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              aria-label={t('Delete')}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
